import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { FaBullhorn, FaCheckCircle, FaClock, FaSearch, FaWhatsapp } from "react-icons/fa";
import { db } from "../firebase";
import { sendInviteAppUsersWhatsapp } from "../services/crmAdminService";

const RECENT_INVITE_MS = 24 * 60 * 60 * 1000;
const PREVIEW_TEMPLATE = "Hi {{1}} ,We noticed you haven't created your Artist Profile on ApnaArtist yet.Complete your profile to become a verified artist and start receiving genuine opportunities.If you face any issue, WhatsApp our support team at +91 7002618625.Tap below to register now.";

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizeUserPhone(raw) {
  const digits = digitsOnly(raw);
  if (!digits) {
    return { valid: false, phoneRaw: String(raw || ""), phoneDigits: "", phoneE164: "" };
  }

  let national = digits;
  if (digits.length === 11 && digits.startsWith("0")) national = digits.slice(1);
  else if (digits.length === 12 && digits.startsWith("91")) national = digits.slice(2);
  else if (digits.length > 10 && digits.startsWith("91")) national = digits.slice(2);

  if (!/^\d{10}$/.test(national)) {
    return {
      valid: false,
      phoneRaw: String(raw || ""),
      phoneDigits: digits,
      phoneE164: "",
    };
  }

  return {
    valid: true,
    phoneRaw: String(raw || "").trim(),
    phoneDigits: national,
    phoneE164: `+91${national}`,
  };
}

function safeName(user = {}) {
  return String(
    user.name ||
      user.fullName ||
      user.full_name ||
      user.username ||
      user.displayName ||
      "App User"
  ).trim() || "App User";
}

function safeEmail(user = {}) {
  return String(user.email || user.mail || "").trim();
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function formatDate(value) {
  const ms = toMillis(value);
  if (!ms) return "—";
  return new Date(ms).toLocaleString("en-IN");
}

function reportLabel(status) {
  const value = String(status || "").trim().toLowerCase();
  if (!value) return "Never invited";
  if (value === "failed") return "Failed";
  if (value === "read") return "Read";
  if (value === "delivered") return "Delivered";
  if (value === "queued") return "Queued";
  if (value === "sent") return "Sent";
  return value.replace(/_/g, " ");
}

function reportBadgeClass(status) {
  const value = String(status || "").trim().toLowerCase();
  if (value === "failed") return "danger";
  if (value === "read" || value === "delivered" || value === "sent") return "ok";
  if (value === "queued") return "info";
  return "warn";
}

export default function InviteAppUsers({ onNotice }) {
  const [users, setUsers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [inviteRecipients, setInviteRecipients] = useState([]);
  const [campaignRecipients, setCampaignRecipients] = useState([]);
  const [activeCampaignId, setActiveCampaignId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const unsubUsers = onSnapshot(collection(db, "users"), (snap) => {
      setUsers(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    });
    const unsubArtists = onSnapshot(collection(db, "artists"), (snap) => {
      setArtists(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    });
    const unsubCampaigns = onSnapshot(query(collection(db, "crm_invite_campaigns"), orderBy("createdAt", "desc")), (snap) => {
      setCampaigns(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    });
    const unsubRecipients = onSnapshot(
      query(
        collectionGroup(db, "recipients"),
        where("messageType", "==", "invite_app_user_artist_profile")
      ),
      (snap) => {
        setInviteRecipients(
          snap.docs.map((item) => {
            const parent = item.ref.parent.parent;
            return {
              id: item.id,
              campaignId: parent?.id || "",
              ...item.data(),
            };
          })
        );
      }
    );

    return () => {
      unsubUsers();
      unsubArtists();
      unsubCampaigns();
      unsubRecipients();
    };
  }, []);

  useEffect(() => {
    if (!activeCampaignId && campaigns[0]?.id) {
      setActiveCampaignId(campaigns[0].id);
    }
  }, [activeCampaignId, campaigns]);

  useEffect(() => {
    if (!activeCampaignId) {
      setCampaignRecipients([]);
      return undefined;
    }
    const unsub = onSnapshot(
      collection(db, "crm_invite_campaigns", activeCampaignId, "recipients"),
      (snap) => {
        setCampaignRecipients(
          snap.docs.map((item) => ({
            id: item.id,
            campaignId: activeCampaignId,
            ...item.data(),
          }))
        );
      }
    );
    return () => unsub();
  }, [activeCampaignId]);

  const artistPhoneSet = useMemo(() => {
    const set = new Set();
    artists.forEach((artist) => {
      [
        artist.user_uid,
        artist.user_uid_normalized,
        artist.phone,
        artist.userPhone,
        artist.mobile,
        artist.contactPhone,
        artist.id,
      ].forEach((value) => {
        const digits = digitsOnly(value);
        if (digits) set.add(digits.length > 10 && digits.startsWith("91") ? digits.slice(-10) : digits);
      });
    });
    return set;
  }, [artists]);

  const artistUidSet = useMemo(() => {
    const set = new Set();
    artists.forEach((artist) => {
      [artist.id, artist.uid].forEach((value) => {
        const next = String(value || "").trim();
        if (next) set.add(next);
      });
    });
    return set;
  }, [artists]);

  const latestInviteByUser = useMemo(() => {
    const map = new Map();
    inviteRecipients.forEach((item) => {
      const key = String(item.userId || "").trim();
      if (!key) return;
      const previous = map.get(key);
      const currentMs = Math.max(
        toMillis(item.readAt),
        toMillis(item.deliveredAt),
        toMillis(item.sentAt),
        toMillis(item.updatedAt),
        toMillis(item.createdAt)
      );
      if (!previous || currentMs > previous._ms) {
        map.set(key, { ...item, _ms: currentMs });
      }
    });
    return map;
  }, [inviteRecipients]);

  const nonArtistUsers = useMemo(() => {
    return users
      .map((user) => {
        const userId = String(user.id || user.uid || "").trim();
        const uid = String(user.uid || "").trim();
        const rawPhone = user.id || user.phone || user.mobile || user.phoneNumber || "";
        const normalizedPhone = normalizeUserPhone(rawPhone);
        const phoneDigits = normalizedPhone.phoneDigits || digitsOnly(rawPhone);
        const isArtist =
          (phoneDigits && artistPhoneSet.has(phoneDigits)) ||
          (uid && artistUidSet.has(uid)) ||
          (userId && artistUidSet.has(userId));
        if (isArtist) return null;

        const latestInvite = latestInviteByUser.get(userId) || null;
        const lastInvitedAt = latestInvite?._ms || 0;
        return {
          id: userId || uid || normalizedPhone.phoneDigits || `${safeName(user)}__${safeEmail(user) || "no-email"}`,
          userId: userId || uid || normalizedPhone.phoneDigits,
          fullName: safeName(user),
          phone: normalizedPhone.phoneRaw || String(rawPhone || "").trim(),
          normalizedPhone: normalizedPhone.phoneE164,
          phoneDigits,
          email: safeEmail(user),
          joinedAt: user.createdAt || null,
          lastInviteStatus: latestInvite?.status || "",
          lastInvitedAt: latestInvite?.readAt || latestInvite?.deliveredAt || latestInvite?.sentAt || latestInvite?.updatedAt || latestInvite?.createdAt || null,
          lastInvitedMs: lastInvitedAt,
          canInvite: normalizedPhone.valid,
          recentInvite: lastInvitedAt > 0 && Date.now() - lastInvitedAt < RECENT_INVITE_MS,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (b.lastInvitedMs || 0) - (a.lastInvitedMs || 0) || a.fullName.localeCompare(b.fullName));
  }, [artistPhoneSet, artistUidSet, latestInviteByUser, users]);

  const filteredUsers = useMemo(() => {
    const queryText = search.trim().toLowerCase();
    if (!queryText) return nonArtistUsers;
    return nonArtistUsers.filter((user) => {
      const haystack = [
        user.fullName,
        user.phone,
        user.email,
        user.userId,
      ].join(" ").toLowerCase();
      return haystack.includes(queryText);
    });
  }, [nonArtistUsers, search]);

  const selectedUsers = useMemo(
    () => nonArtistUsers.filter((user) => selectedIds.includes(user.id)),
    [nonArtistUsers, selectedIds]
  );

  const selectedEligibleUsers = useMemo(
    () => selectedUsers.filter((user) => user.canInvite),
    [selectedUsers]
  );

  const previewUser = selectedUsers[0] || filteredUsers[0] || null;
  const eligibleUserIds = nonArtistUsers.filter((user) => user.canInvite).map((user) => user.id);
  const allEligibleSelected = eligibleUserIds.length > 0 && eligibleUserIds.every((id) => selectedIds.includes(id));

  const activeCampaign = useMemo(
    () => campaigns.find((item) => item.id === activeCampaignId) || null,
    [activeCampaignId, campaigns]
  );

  const reportSummary = useMemo(() => {
    const summary = campaignRecipients.reduce((acc, item) => {
      const status = String(item.status || "").toLowerCase();
      if (status === "queued") acc.queued += 1;
      else if (status === "sent") acc.sent += 1;
      else if (status === "failed") acc.failed += 1;
      else if (status === "skipped") acc.skipped += 1;
      else if (status === "dry_run") acc.dryRun += 1;

      if (status === "delivered" || item.deliveredAt) acc.delivered += 1;
      if (status === "read" || item.readAt) acc.read += 1;
      return acc;
    }, {
      queued: 0,
      sent: 0,
      failed: 0,
      delivered: 0,
      read: 0,
      skipped: 0,
      dryRun: 0,
    });

    const recipientTotal = campaignRecipients.length;
    const recipientValid = Math.max(0, recipientTotal - summary.skipped);
    const totalRequested = Number(activeCampaign?.totalRequested || recipientTotal || 0);
    const totalValid = Number(recipientTotal ? recipientValid : (activeCampaign?.totalValid || activeCampaign?.total || 0));
    return {
      ...summary,
      totalRequested,
      totalValid,
      recipientsCount: Number(recipientTotal || activeCampaign?.recipientsCount || 0),
    };
  }, [activeCampaign, campaignRecipients]);

  const campaignLogs = useMemo(() => {
    return [...campaignRecipients]
      .sort((a, b) => {
        const aMs = Math.max(toMillis(a.readAt), toMillis(a.deliveredAt), toMillis(a.sentAt), toMillis(a.updatedAt), toMillis(a.createdAt));
        const bMs = Math.max(toMillis(b.readAt), toMillis(b.deliveredAt), toMillis(b.sentAt), toMillis(b.updatedAt), toMillis(b.createdAt));
        return bMs - aMs;
      });
  }, [campaignRecipients]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production" || !activeCampaign) return;
    const docSent = Number(activeCampaign.sent || 0);
    const docValid = Number(activeCampaign.totalValid || activeCampaign.total || 0);
    if (docValid > 0 && docSent > docValid) {
      // Keep the UI safe by rendering recipient-derived totals, and surface the mismatch only in local/dev builds.
      console.warn("Invite campaign counter mismatch", {
        campaignId: activeCampaign.id,
        campaignSent: docSent,
        campaignTotalValid: docValid,
        recipientSent: reportSummary.sent,
        recipientsCount: campaignRecipients.length,
      });
    }
  }, [activeCampaign, campaignRecipients.length, reportSummary.sent]);

  async function handleInvite(targetUsers, modeLabel) {
    const sendable = targetUsers.filter((user) => user.canInvite);
    if (!sendable.length) {
      if (typeof onNotice === "function") onNotice("Select at least one user with a valid phone number.", "err");
      return;
    }

    const recentUsers = sendable.filter((user) => user.recentInvite);
    const confirmText = recentUsers.length
      ? `${modeLabel} includes ${recentUsers.length} user(s) invited in the last 24 hours. Continue?`
      : `Send ${modeLabel.toLowerCase()} now?`;
    if (!window.confirm(confirmText)) return;

    setSending(true);
    try {
      const payload = sendable.map((user) => ({
        userId: user.userId,
        fullName: user.fullName,
        phone: user.phone,
      }));
      const result = await sendInviteAppUsersWhatsapp(payload);
      if (result?.success === false) {
        throw new Error(result.message || "Unable to send invite campaign.");
      }
      if (result?.campaignId) {
        setActiveCampaignId(result.campaignId);
      }
      if (typeof onNotice === "function") {
        const skipped = Number(result?.skipped || 0);
        const totalValid = Number(result?.totalValid || sendable.length);
        const sent = Number(result?.sent || 0);
        const failed = Number(result?.failed || 0);
        onNotice(`Invite campaign created: ${totalValid} valid, ${sent} sent, ${failed} failed${skipped ? `, ${skipped} skipped` : ""}.`);
      }
    } catch (error) {
      if (typeof onNotice === "function") onNotice(error?.message || "Unable to send invite campaign.", "err");
    } finally {
      setSending(false);
    }
  }

  function toggleUser(id) {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]
    ));
  }

  function toggleSelectAllEligible() {
    setSelectedIds((prev) => (
      allEligibleSelected
        ? prev.filter((id) => !eligibleUserIds.includes(id))
        : Array.from(new Set([...prev, ...eligibleUserIds]))
    ));
  }

  return (
    <div className="crm-invite-grid">
      <div className="crm-panel crm-invite-main">
        <div className="crm-invite-header">
          <div>
            <p className="crm-kicker crm-kicker-dark">Invite App-users</p>
            <h2 className="crm-invite-title">Invite app users to become an artist</h2>
            <p className="crm-panel-sub" style={{ marginBottom: 0 }}>
              Real-time list of registered app users who have not created an artist profile yet.
            </p>
          </div>
          <div className="crm-chip-row">
            <span className="crm-chip"><FaBullhorn /> {nonArtistUsers.length} non-artist users</span>
            <span className="crm-chip"><FaCheckCircle /> {selectedEligibleUsers.length} selected</span>
          </div>
        </div>

        <div className="crm-filters" style={{ marginTop: 14 }}>
          <div className="crm-search-wrap">
            <FaSearch />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name, phone, email"
            />
          </div>
          <button type="button" className="crm-btn-secondary" onClick={toggleSelectAllEligible}>
            {allEligibleSelected ? "Clear Eligible Selection" : "Select All Eligible"}
          </button>
          <button
            type="button"
            className="crm-btn"
            onClick={() => handleInvite(selectedUsers, `bulk invite to ${selectedEligibleUsers.length} user(s)`)}
            disabled={sending || !selectedEligibleUsers.length}
          >
            <FaWhatsapp /> {sending ? "Sending..." : "Bulk Invite"}
          </button>
        </div>

        <div className="crm-table-wrap" style={{ marginTop: 14 }}>
          <table className="crm-table crm-invite-table">
            <thead>
              <tr>
                <th><input type="checkbox" checked={allEligibleSelected} onChange={toggleSelectAllEligible} /></th>
                <th>Full name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Joined date</th>
                <th>Last invite status</th>
                <th>Last invited at</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length ? filteredUsers.map((user) => (
                <tr key={user.id} className={!user.canInvite ? "crm-row-muted" : ""}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(user.id)}
                      onChange={() => toggleUser(user.id)}
                    />
                  </td>
                  <td>
                    {user.fullName}
                    <small>{user.userId || "—"}</small>
                    {user.recentInvite ? <span className="crm-chip crm-chip-warn"><FaClock /> invited in last 24h</span> : null}
                  </td>
                  <td>
                    {user.normalizedPhone || user.phone || "—"}
                    <small>{user.canInvite ? "Ready for WhatsApp" : "Missing/invalid phone"}</small>
                  </td>
                  <td>{user.email || "—"}</td>
                  <td>{formatDate(user.joinedAt)}</td>
                  <td>
                    <span className={`crm-badge ${reportBadgeClass(user.lastInviteStatus)}`}>
                      {reportLabel(user.lastInviteStatus)}
                    </span>
                  </td>
                  <td>{formatDate(user.lastInvitedAt)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7}>No matching non-artist users found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="crm-panel crm-invite-side">
        <h3>Invite Preview</h3>
        {previewUser ? (
          <>
            <div className="crm-count-grid">
              <div className="crm-preview-stat">
                <span>Name</span>
                <strong>{previewUser.fullName}</strong>
              </div>
              <div className="crm-preview-stat">
                <span>Phone</span>
                <strong>{previewUser.normalizedPhone || previewUser.phone || "—"}</strong>
              </div>
            </div>

            <div className="crm-preview-box">
              {PREVIEW_TEMPLATE.replace("{{1}}", previewUser.fullName)}
            </div>

            <button
              type="button"
              className="crm-btn"
              onClick={() => handleInvite([previewUser], `invite for ${previewUser.fullName}`)}
              disabled={sending || !previewUser.canInvite}
            >
              <FaWhatsapp /> {sending ? "Sending..." : "Invite Selected User"}
            </button>
            {!previewUser.canInvite ? (
              <p className="crm-note" style={{ marginTop: 10 }}>
                Invite is disabled because this user does not have a valid Indian mobile number.
              </p>
            ) : null}
          </>
        ) : (
          <div className="crm-empty">Select a user to preview the invite message.</div>
        )}
      </div>

      <div className="crm-panel crm-invite-report">
        <div className="crm-toolbar">
          <div>
            <h3 style={{ marginBottom: 6 }}>Live Invite Report</h3>
            <p className="crm-panel-sub" style={{ marginBottom: 0 }}>
              Totals are based on the selected campaign recipients only.
            </p>
          </div>
          <select
            value={activeCampaignId}
            onChange={(event) => setActiveCampaignId(event.target.value)}
            style={{ maxWidth: 260 }}
          >
            <option value="">Select campaign</option>
            {campaigns.map((item) => (
              <option key={item.id} value={item.id}>
                {formatDate(item.createdAt)} | {item.totalValid || item.total || 0} valid
              </option>
            ))}
          </select>
        </div>

        <div className="crm-count-grid" style={{ marginTop: 14 }}>
          <ReportStat label="Requested" value={reportSummary.totalRequested} />
          <ReportStat label="Valid" value={reportSummary.totalValid} />
          <ReportStat label="Recipients" value={reportSummary.recipientsCount} />
          <ReportStat label="Queued" value={reportSummary.queued} />
          <ReportStat label="Sent" value={reportSummary.sent} />
          <ReportStat label="Failed" value={reportSummary.failed} />
          <ReportStat label="Delivered" value={reportSummary.delivered} />
          <ReportStat label="Read" value={reportSummary.read} />
          <ReportStat label="Skipped" value={reportSummary.skipped} />
        </div>

        <div className="crm-table-wrap" style={{ marginTop: 14 }}>
          <table className="crm-table" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>User</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Campaign</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {campaignLogs.length ? campaignLogs.map((item) => (
                <tr key={`${item.campaignId}-${item.id}`}>
                  <td>{item.fullName || "App User"}</td>
                  <td>{item.normalizedPhone || item.phone || "—"}</td>
                  <td><span className={`crm-badge ${reportBadgeClass(item.status)}`}>{reportLabel(item.status)}</span></td>
                  <td>{item.campaignId || "—"}</td>
                  <td>{formatDate(item.readAt || item.deliveredAt || item.sentAt || item.updatedAt || item.createdAt)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={5}>No recipients found for the selected campaign.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReportStat({ label, value }) {
  return (
    <div className="crm-preview-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
